import { parseCard } from '../../../../packages/core/src/ratecard.js';
import { ApiError, get, patch, post, type Me } from '../api.js';
import {
  button, chip, clear, drawer, el, empty, field, fmtMoney, input, mono, panel, select,
  skeleton, table, toast
} from '../ui.js';
import { STATES } from '../../../../packages/core/src/states.js';

/**
 * What each carrier sells, and what it costs.
 *
 * One section per carrier, its products under it, its rate card under each
 * product — which is the shape an agency already thinks in, because it is the
 * shape the carrier's own guide is printed in.
 *
 * THE RATE CARD IS THE POINT
 * Cellvora has no rates of its own and buys none. An appointed agency already
 * holds every card it needs; loading one here is what turns the quoter from a
 * list of possibilities into real premiums. A product with no card is not
 * broken — it simply sends the agent to the carrier's own tool instead of
 * inventing a number.
 */

const PLACES: Array<[string, string]> = [...STATES]
  .sort((a, b) => a.name.localeCompare(b.name, 'en'))
  .map((s) => [s.code, s.name]);

interface Carrier { id: string; name: string; archivedAt: string | null }
interface Product {
  id: string; carrierId: string; name: string; productType: string;
  underwriting: string; minAge: number; maxAge: number;
  minFaceCents: number; maxFaceCents: number; termYears: number[];
  tobaccoAllowed: boolean; states: string[]; rateMode: 'table' | 'per_thousand';
  policyFeeCents: number; quoteUrl: string | null; notes: string | null;
  /** Null until somebody has checked these limits against the carrier's guide. */
  verifiedAt: string | null;
  verifiedBy: string | null;
  sourceNote: string | null;
  archivedAt: string | null;
}
interface Ref {
  productTypes: Array<{ code: string; label: string }>;
  underwritingKinds: Array<{ code: string; label: string }>;
  rateModes: Array<{ code: string; label: string }>;
}

const toCents = (raw: string): number | null => {
  const s = String(raw).replace(/[$,\s]/g, '').trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
};
const dollars = (c: number): string => String(Math.round(c) / 100);

/* ------------------------------------------------------------------ view -- */

export function productsPanel(me: Me): HTMLElement {
  const host = el('div');
  const body = el('div');
  const mayEdit = me.capabilities.includes('config.production');
  host.append(body);

  async function load(): Promise<void> {
    clear(body);
    body.append(skeleton(4));
    const [carriers, products, ref] = await Promise.all([
      get<Carrier[]>('/v1/carriers').catch(() => [] as Carrier[]),
      get<Product[]>('/v1/products').catch(() => [] as Product[]),
      get<Ref>('/v1/reference'),
    ]);
    clear(body);

    const live = carriers.filter((c) => !c.archivedAt);
    if (!live.length) {
      body.append(empty('No carriers yet',
        'Add the carriers you write first, on the Carriers tab. Products and rate cards hang '
        + 'underneath them.'));
      return;
    }

    if (mayEdit) {
      const anyProducts = products.some((p) => !p.archivedAt);
      body.append(el('div', {
        style: 'display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-bottom:14px',
      },
      button(anyProducts ? 'Paste a list of products' : 'Paste your whole catalogue', anyProducts ? 'sm' : 'sm pri',
        () => openBulk(live, ref, load)),
      el('span', {
        style: 'font-size:12px;color:var(--ink-3)',
        text: anyProducts
          ? 'Adds several at once from a spreadsheet or a typed list.'
          : 'Faster than the form if you have more than two or three to add.',
      })));
    }

    for (const carrier of live) {
      const mine = products.filter((p) => p.carrierId === carrier.id && !p.archivedAt);
      const section = el('div');

      if (!mine.length) {
        section.append(el('p', {
          style: 'font-size:12.5px;color:var(--ink-3)',
          text: 'Nothing set up yet. Add the products you write with them — the issue ages and '
            + 'face limits off their own guide are what decide who this carrier can be quoted for.',
        }));
      } else {
        section.append(table(['Product', 'Issue ages', 'Face', 'Rate card', ''],
          await Promise.all(mine.map(async (p) => {
            const count = await get<{ count: number }>(`/v1/products/${p.id}/rates/count`)
              .then((r) => r.count).catch(() => 0);
            return {
              cells: [
                el('div', null,
                  el('b', { text: p.name }),
                  el('div', {
                    style: 'font-size:11.5px;color:var(--ink-4)',
                    text: `${labelOf(ref.productTypes, p.productType)} · ${labelOf(ref.underwritingKinds, p.underwriting)}`,
                  })),
                `${p.minAge}–${p.maxAge}`,
                mono(`${fmtMoney(p.minFaceCents)}–${fmtMoney(p.maxFaceCents)}`),
                // Two different facts, and they are not the same one. A rate
                // card decides the premium; the issue limits decide whether the
                // carrier is offered at all. A product can have a perfect card
                // and limits nobody ever checked.
                el('div', { style: 'display:flex;flex-direction:column;gap:4px;align-items:flex-start' },
                  count ? chip(`${count.toLocaleString()} rates`, 'good') : chip('no card', 'warn'),
                  p.verifiedAt ? chip('limits checked', 'good') : chip('limits unchecked', 'crit')),
                mayEdit
                  ? el('div', { style: 'display:flex;gap:6px;flex-wrap:wrap' },
                    button('Rate card', 'sm pri', () => openRates(p, load)),
                    button(p.verifiedAt ? 'Limits' : 'Check limits', p.verifiedAt ? 'sm ghost' : 'sm', () => openVerify(p, load)),
                    button('Edit', 'sm ghost', () => openProduct(carrier, p, ref, load)),
                    // Archived, never deleted — the same rule as carriers. A
                    // product an agency stops writing is still on every case
                    // already written against it and every quote that priced
                    // it. Archiving stops it being offered on new ones; it
                    // does not rewrite history and must not read as though it
                    // does.
                    button('Archive', 'sm ghost', async () => {
                      if (!confirm(
                        `Archive ${p.name}?\n\n`
                        + 'It stops being quoted and stops appearing on new cases. Cases and quotes '
                        + 'already written against it are untouched, and its rate card is kept.',
                      )) return;
                      try {
                        await post(`/v1/products/${p.id}/archive`, {});
                        toast(`${p.name} archived.`, 'ok');
                        await load();
                      } catch (e) {
                        toast(e instanceof ApiError ? e.message : 'Could not archive it.', 'crit');
                      }
                    }))
                  : el('span'),
              ],
            };
          }))));
      }

      // Archived products are filtered out of the table above, which would
      // otherwise make archiving look like deletion — the product simply
      // disappears and nothing says where it went. They stay findable.
      const archived = products.filter((p) => p.carrierId === carrier.id && p.archivedAt);
      if (archived.length) {
        const list = el('div', { hidden: true });
        for (const p of archived) {
          list.append(el('div', {
            style: 'font-size:12.5px;color:var(--ink-4);padding:4px 0',
            text: `${p.name} — archived ${(p.archivedAt ?? '').slice(0, 10)}`,
          }));
        }
        const toggle = button(`Show ${archived.length} archived`, 'sm ghost', () => {
          list.hidden = !list.hidden;
          toggle.textContent = list.hidden ? `Show ${archived.length} archived` : 'Hide archived';
        });
        section.append(el('div', { style: 'margin-top:12px' }, toggle), list);
      }

      if (mayEdit) {
        section.append(el('div', { style: 'margin-top:12px' },
          button('Add a product', 'sm', () => openProduct(carrier, null, ref, load))));
      }

      body.append(panel(carrier.name, section));
    }

    if (!mayEdit) {
      body.append(el('p', {
        style: 'font-size:12.5px;color:var(--ink-3)',
        text: 'Only an owner or administrator can change what your carriers sell or load a rate card.',
      }));
    }
  }

  void load();
  return host;
}

const labelOf = (list: Array<{ code: string; label: string }>, code: string): string =>
  list.find((x) => x.code === code)?.label ?? code;

/* --------------------------------------------------------------- product -- */

function openProduct(
  carrier: Carrier, existing: Product | null, ref: Ref, done: () => Promise<void>,
): void {
  const body = el('div');
  const close = drawer(existing ? `Edit ${existing.name}` : `A product from ${carrier.name}`, body);
  const p = existing;

  const name = input({ value: p?.name ?? '', placeholder: 'Living Promise, Trendsetter Super…' });
  const productType = select(ref.productTypes.map((t) => [t.code, t.label] as [string, string]),
    p?.productType ?? 'final_expense');
  const underwriting = select(ref.underwritingKinds.map((u) => [u.code, u.label] as [string, string]),
    p?.underwriting ?? 'simplified');
  const minAge = input({ type: 'number', min: '0', max: '120', value: String(p?.minAge ?? 50) });
  const maxAge = input({ type: 'number', min: '0', max: '120', value: String(p?.maxAge ?? 85) });
  const minFace = input({ value: p ? dollars(p.minFaceCents) : '2000', inputmode: 'decimal' });
  const maxFace = input({ value: p ? dollars(p.maxFaceCents) : '40000', inputmode: 'decimal' });
  const terms = input({ value: (p?.termYears ?? []).join(', '), placeholder: '10, 20, 30' });
  const tobaccoAllowed = select([['true', 'Yes'], ['false', 'No']], String(p?.tobaccoAllowed ?? true));
  const rateMode = select(ref.rateModes.map((m) => [m.code, m.label] as [string, string]),
    p?.rateMode ?? 'table');
  const policyFee = input({ value: p ? dollars(p.policyFeeCents) : '0', inputmode: 'decimal' });
  const quoteUrl = input({ value: p?.quoteUrl ?? '', placeholder: 'https://…' });
  const notes = el('textarea', { class: 'inp', rows: '2' }) as HTMLTextAreaElement;
  notes.value = p?.notes ?? '';

  const stateBox = el('div', { class: 'state-grid' });
  const chosen = new Set(p?.states ?? []);
  for (const [code, label] of PLACES) {
    const cb = input({ type: 'checkbox', checked: chosen.has(code) });
    cb.addEventListener('change', () => (cb.checked ? chosen.add(code) : chosen.delete(code)));
    stateBox.append(el('label', { class: 'state-pick' }, cb, el('span', { text: label })));
  }

  const save = button(p ? 'Save' : 'Add it', 'pri', async () => {
    save.disabled = true;
    const payload: Record<string, unknown> = {
      name: name.value.trim(),
      productType: productType.value,
      underwriting: underwriting.value,
      minAge: Number(minAge.value),
      maxAge: Number(maxAge.value),
      minFaceCents: toCents(minFace.value) ?? 0,
      maxFaceCents: toCents(maxFace.value) ?? 0,
      termYears: terms.value.split(',').map((t) => Number(t.trim())).filter((t) => t > 0),
      tobaccoAllowed: tobaccoAllowed.value === 'true',
      // Empty means wherever the producer is licensed, which is the common case.
      states: [...chosen],
      rateMode: rateMode.value,
      policyFeeCents: toCents(policyFee.value) ?? 0,
      quoteUrl: quoteUrl.value.trim() || undefined,
      notes: notes.value.trim() || undefined,
    };
    try {
      if (p) await patch(`/v1/products/${p.id}`, payload);
      else await post('/v1/products', { carrierId: carrier.id, ...payload });
      close();
      await done();
      toast(p ? 'Saved.' : 'Product added.', 'ok');
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not save that product.', 'crit');
      save.disabled = false;
    }
  });

  body.append(
    el('p', {
      style: 'font-size:12.5px;color:var(--ink-3);margin-bottom:14px',
      text: 'Copy the issue rules straight off the carrier’s own guide. They are what decide who '
        + 'this product can be quoted for, so a wrong age band here quietly hides a carrier that '
        + 'could have written the case.',
    }),
    el('div', { class: 'filters' }, field('Name', name), field('Kind', productType), field('Underwriting', underwriting)),
    el('div', { class: 'filters' },
      field('Youngest issue age', minAge), field('Oldest issue age', maxAge),
      field('Smallest face ($)', minFace), field('Largest face ($)', maxFace)),
    el('div', { class: 'filters' },
      field('Term lengths', terms, 'Comma separated. Leave empty if it is not term.'),
      field('Writes tobacco', tobaccoAllowed),
      field('Policy fee ($ a month)', policyFee, 'Usually only on term.')),
    el('div', { class: 'filters' },
      field('How its rates are printed', rateMode),
      field('Carrier’s own quoter', quoteUrl, 'Where to price it until a card is loaded.')),
    field('Notes', notes),
    el('div', { class: 'field' },
      el('span', { text: 'States it is filed in' }),
      el('small', { text: 'Leave every box unticked for “wherever the producer is licensed”, which is usually right.' }),
      stateBox),
    save,
  );
}

/* ------------------------------------------------------------- rate card -- */

/**
 * Loading a card.
 *
 * Paste, because every agency already has these as a spreadsheet or a PDF table
 * and retyping a few thousand cells by hand is how a rate card gets a typo in
 * it. The columns are named explicitly rather than guessed at: a mis-mapped
 * rate column is a wrong premium said out loud to a family, and that is not a
 * mistake worth being clever about.
 */
/**
 * Say that somebody has checked a product's issue limits against the carrier's
 * own guide.
 *
 * Its own drawer rather than a checkbox on the edit form, because it is not an
 * edit. Editing the limits and vouching for them are opposite acts, and putting
 * them on one screen invites somebody to change a number and tick the box in
 * the same motion — which is how a product ends up marked checked by the person
 * who guessed it.
 */
function openVerify(product: Product, done: () => Promise<void>): void {
  const body = el('div');
  const close = drawer(`Issue limits — ${product.name}`, body);

  const on = Boolean(product.verifiedAt);
  body.append(el('div', { class: on ? 'okbox' : 'warnbox' },
    el('b', null, on ? 'Checked. ' : 'Not checked. '),
    on
      ? `These limits were confirmed against a document${product.sourceNote ? `: ${product.sourceNote}` : ''}.`
      : 'Nobody has confirmed these limits against this carrier\u2019s guide. They are still used '
        + 'to decide who this carrier can be quoted for, so an ages or face amount that is wrong '
        + 'here turns into a confident wrong answer \u2014 in either direction.'));

  body.append(el('p', { class: 's', style: 'font-size:12.5px;color:var(--ink-3);margin:14px 0 4px' },
    `Currently: ages ${product.minAge}\u2013${product.maxAge}, `
    + `${fmtMoney(product.minFaceCents)} to ${fmtMoney(product.maxFaceCents)}. `
    + 'Check these against the carrier\u2019s guide before confirming. If they are wrong, close this '
    + 'and use Edit instead.'));

  const note = input({ value: product.sourceNote ?? '', placeholder: 'Living Promise producer guide, rev 04/2026, p.7' });
  body.append(field('Which document did you check?', note,
    'A guide name and revision is enough \u2014 something the next person can go and find.'));

  const save = button(on ? 'Update the source' : 'Confirm these limits', 'pri');
  save.addEventListener('click', async () => {
    save.disabled = true;
    try {
      await post(`/v1/products/${product.id}/verify`, { verified: true, sourceNote: note.value });
      toast('Limits confirmed.', 'ok');
      close(); await done();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Could not save that.', 'crit');
      save.disabled = false;
    }
  });

  const row = el('div', { style: 'display:flex;gap:8px;flex-wrap:wrap;margin-top:14px' }, save);
  if (on) {
    row.append(button('Mark unchecked', 'danger', async () => {
      try {
        await post(`/v1/products/${product.id}/verify`, { verified: false });
        toast('Marked unchecked.', 'ok');
        close(); await done();
      } catch (e) { toast(e instanceof ApiError ? e.message : 'Could not change it.', 'crit'); }
    }));
  }
  body.append(row);
}

function openRates(product: Product, done: () => Promise<void>): void {
  const body = el('div');
  const close = drawer(`Rate card — ${product.name}`, body);

  const perThousand = product.rateMode === 'per_thousand';
  /*
   * Who the grid is for.
   *
   * A carrier prints one table per sex and tobacco status — "Female
   * Non-Tobacco" across the top of the page — with ages down the side and face
   * amounts across. Those two facts are on the page, not in the table, so a
   * pasted grid cannot carry them and they are asked for here instead.
   *
   * Ignored entirely when the paste is already long-format with its own sex
   * and tobacco columns.
   */
  const gridSex = select([['female', 'Female'], ['male', 'Male']], 'female');
  const gridTob = select([['false', 'Non-tobacco'], ['true', 'Tobacco']], 'false');
  const gridClass = input({ placeholder: 'Standard' });

  /*
   * Where this card came from.
   *
   * The API has always accepted both of these and this screen never asked, so
   * every card ever loaded here recorded nothing about its own origin — and the
   * quoter now marks exactly those premiums "rate not sourced" against the
   * number on screen. Without these two fields that mark could never be
   * cleared, which would make it noise inside a week.
   *
   * Asked at load time and not later, because this is the only moment anybody
   * has the document open. Come back tomorrow and the honest answer to "which
   * sheet was this?" is already gone.
   *
   * Both optional in the API and left optional here. A card typed off a screen
   * share with no date on it is a real situation, and the quoter saying "nobody
   * recorded where this came from" is more useful than a date somebody invented
   * to get past a required field.
   */
  const srcNote = input({ placeholder: 'Living Promise producer guide, rev 04/2026, p.7' });
  const srcDate = input({ placeholder: 'YYYY-MM-DD' });
  srcDate.type = 'date';

  const paste = el('textarea', {
    class: 'inp', rows: '12', spellcheck: 'false',
    placeholder: perThousand
      ? 'age,10,20,30\n58,1.42,1.71,2.24\n59,1.49,1.80,2.36'
      : 'age,5000,10000,15000,20000\n58,31.02,58.42,85.82,113.22\n59,32.41,61.09,89.77,118.45',
  }) as HTMLTextAreaElement;
  paste.style.fontFamily = 'var(--f-mono)';
  paste.style.fontSize = '12px';
  const out = el('div', { style: 'margin-top:12px' });

  const load = button('Load this card', 'pri', async () => {
    load.disabled = true;
    clear(out);
    try {
      const rows = parseCard(paste.value, product, {
        sex: gridSex.value as 'male' | 'female',
        tobacco: gridTob.value === 'true',
        rateClass: gridClass.value.trim() || undefined,
      });
      const note = srcNote.value.trim();
      const on = srcDate.value.trim();
      const res = await post<{ loaded: number }>(`/v1/products/${product.id}/rates`, {
        rows,
        // Omitted rather than sent empty. The API treats absent as "nobody
        // recorded one", which is the truth; sending '' would be a value.
        ...(note ? { sourceNote: note } : {}),
        ...(on ? { effectiveOn: on } : {}),
      });
      close();
      await done();
      toast(
        note || on
          ? `${res.loaded.toLocaleString()} rates loaded, with their source.`
          : `${res.loaded.toLocaleString()} rates loaded — no source recorded, so the quoter `
            + 'will mark these premiums untraceable.',
        note || on ? 'ok' : 'crit',
      );
    } catch (e) {
      out.append(el('div', { class: 'warnbox' },
        e instanceof ApiError ? e.message : (e as Error).message));
      load.disabled = false;
    }
  });

  body.append(
    el('div', { class: 'okbox' },
      el('b', null, 'These are your carrier’s own rates. '),
      'Cellvora holds none of its own and buys none. Paste the card in and the quoter prices from '
      + 'it exactly as printed — nothing is interpolated between ages or face amounts, because a '
      + 'premium halfway between two printed rows is one no carrier will honour.'),
    el('p', {
      style: 'font-size:12.5px;color:var(--ink-3);margin:14px 0 10px',
      text: perThousand
        ? 'Paste the table as the carrier prints it: ages down the left, term lengths across the '
          + 'top, the monthly cost per $1,000 in each cell.'
        : 'Paste the table as the carrier prints it: ages down the left, face amounts across the '
          + 'top, the monthly premium in each cell.',
    }),
    el('div', { class: 'filters' },
      field('This table is for', gridSex, 'Carriers print one table per sex.'),
      field('Tobacco', gridTob),
      field('Class (optional)', gridClass, 'If the card prints more than one.')),
    paste,
    el('div', { class: 'filters', style: 'margin-top:14px' },
      field('Which document is this from?', srcNote,
        'Name the sheet and the revision. This is what lets an agent say where a premium '
        + 'came from a year from now.'),
      field('Effective date', srcDate, 'The date the card takes effect, if it is printed on it.')),
    el('div', { style: 'margin-top:12px' }, load),
    out,
    el('details', { style: 'margin-top:14px' },
      el('summary', { style: 'font-size:12.5px;color:var(--ink-3);cursor:pointer', text: 'Already have it one-row-per-rate?' }),
      el('div', { style: 'font-size:12px;color:var(--ink-3);line-height:1.7;margin-top:8px' },
        el('div', { text: 'That works too, and takes its own sex and tobacco from the rows:' }),
        el('pre', {
          style: 'font-family:var(--f-mono);font-size:11.5px;margin-top:6px;white-space:pre-wrap',
          text: perThousand
            ? 'sex,tobacco,age,term,rate\nfemale,no,58,20,1.42'
            : 'sex,tobacco,age,face,monthly\nfemale,no,58,10000,58.42',
        }),
        el('div', { style: 'margin-top:8px', text: 'The boxes above are ignored in that case.' }))),
  );
}


/* ------------------------------------------------------- the whole catalogue -- */

/**
 * Adding a catalogue in one paste.
 *
 * Seven carriers, a few products each, eleven fields apiece is most of an hour
 * of form-filling and an easy place to give up. This takes the same rows out of
 * a spreadsheet, an email from an FMO, or a list typed by somebody who knows
 * the answers.
 *
 * IT WILL NOT INVENT A SINGLE FIGURE. Issue ages and face limits decide who a
 * product may be offered to, so every one of them has to come from the row. A
 * missing column is refused, not defaulted: a product silently created as
 * "18–85, any amount" would quietly let an agent quote somebody the carrier
 * will decline, and nothing on the screen would ever say so.
 *
 * Same all-or-nothing rule as the rate card: one bad row rejects the paste. A
 * catalogue that loads "mostly" hides its own gaps.
 */
function openBulk(carriers: Carrier[], ref: Ref, done: () => Promise<void>): void {
  const body = el('div');
  const close = drawer('Paste a list of products', body);

  const paste = el('textarea', {
    class: 'inp', rows: '12',
    placeholder: 'carrier, product, type, underwriting, min age, max age, min face, max face\n'
      + 'Mutual of Omaha, Living Promise, final_expense, simplified, 45, 85, 2000, 40000\n'
      + 'Americo, Eagle Premier, whole_life, simplified, 50, 85, 2000, 30000',
  }) as HTMLTextAreaElement;
  paste.style.fontFamily = 'var(--f-mono)';
  paste.style.fontSize = '12px';

  const out = el('div', { style: 'margin-top:12px' });
  let staged: Array<Record<string, unknown>> = [];

  const create = button('Add them', 'pri', async () => {
    if (!staged.length) return;
    create.disabled = true;
    const failed: string[] = [];
    let made = 0;
    for (const row of staged) {
      try {
        const res = await post<{ id: string }>('/v1/products', row);
        void res;
        made += 1;
      } catch (e) {
        failed.push(`${String(row.name)}: ${e instanceof ApiError ? e.message : 'could not be added'}`);
      }
    }
    if (failed.length) {
      // Reported one by one. Some may already exist, and "8 of 11 added" with
      // no names is not something anybody can act on.
      clear(out);
      out.append(el('div', { class: 'warnbox' },
        el('b', null, `${made} added, ${failed.length} not. `),
        ...failed.map((f) => el('div', { style: 'margin-top:4px', text: f }))));
      create.disabled = false;
      await done();
      return;
    }
    close();
    toast(`${made} product${made === 1 ? '' : 's'} added.`, 'ok');
    await done();
  });
  create.disabled = true;

  const check = button('Check it', 'sm', () => {
    clear(out);
    staged = [];
    create.disabled = true;
    try {
      staged = parseCatalogue(paste.value, carriers, ref);
    } catch (e) {
      out.append(el('div', { class: 'warnbox' }, e instanceof Error ? e.message : 'Could not read that.'));
      return;
    }
    const byCarrier = new Map<string, string[]>();
    for (const row of staged) {
      const name = carriers.find((c) => c.id === row.carrierId)?.name ?? '?';
      byCarrier.set(name, [...(byCarrier.get(name) ?? []), String(row.name)]);
    }
    // Shown before anything is written. This is agency configuration, and a
    // preview is the difference between a mistake you catch and one you find
    // three weeks later in a quote.
    out.append(el('div', { class: 'okbox' },
      el('b', null, `${staged.length} product${staged.length === 1 ? '' : 's'} ready. `),
      'Nothing has been added yet.'));
    out.append(table(['Carrier', 'Products'],
      [...byCarrier.entries()].map(([name, list]) => ({
        cells: [name, list.join(', ')],
      }))));
    create.disabled = false;
  });

  body.append(
    el('p', {
      style: 'font-size:12.5px;color:var(--ink-3);margin-bottom:12px',
      text: 'One product per line, separated by commas or tabs — straight out of a spreadsheet '
        + 'works. The first line names the columns.',
    }),
    el('div', { class: 'note', style: 'margin-bottom:12px' },
      el('b', null, 'Issue ages and face limits are required. '),
      'They decide who each product can be quoted for, so they have to come off the carrier’s '
      + 'own guide. Nothing here is filled in for you.'),
    field('The list', paste),
    el('div', { style: 'display:flex;gap:8px;flex-wrap:wrap' }, check, create),
    out,
    el('details', { style: 'margin-top:14px' },
      el('summary', { style: 'font-size:12.5px;color:var(--ink-3);cursor:pointer', text: 'Which columns are understood' }),
      el('div', { style: 'font-size:12px;color:var(--ink-3);line-height:1.7;margin-top:8px' },
        el('div', { text: 'Required: carrier, product, type, underwriting, min age, max age, min face, max face' }),
        el('div', { text: 'Optional: tobacco (yes/no), term (e.g. "10 20 30"), quote url, rate mode, policy fee, notes' }),
        el('div', { style: 'margin-top:8px', text: `Types: ${ref.productTypes.map((t) => t.code).join(', ')}` }),
        el('div', { text: `Underwriting: ${ref.underwritingKinds.map((t) => t.code).join(', ')}` }),
        el('div', { style: 'margin-top:8px', text: 'A carrier name must already exist on the Carriers tab — names are matched, never created, so a typo cannot split a carrier in two.' }))),
  );
}

/** Reads a pasted catalogue. Throws on the first thing it cannot be sure of. */
function parseCatalogue(
  text: string, carriers: Carrier[], ref: Ref,
): Array<Record<string, unknown>> {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) throw new Error('Paste the list in, with its column names on the first line.');

  const split = (l: string): string[] => l.split(/[,\t]/).map((c) => c.trim());
  const head = split(lines[0]!).map((h) => h.toLowerCase().replace(/[_\s]+/g, ' ').trim());
  const col = (...names: string[]): number => {
    for (const n of names) { const i = head.indexOf(n); if (i !== -1) return i; }
    return -1;
  };

  const iCarrier = col('carrier', 'company');
  const iName = col('product', 'name', 'product name', 'plan');
  const iType = col('type', 'product type', 'kind');
  const iUw = col('underwriting', 'uw', 'underwriting kind');
  const iMinAge = col('min age', 'minage', 'from age', 'issue age min');
  const iMaxAge = col('max age', 'maxage', 'to age', 'issue age max');
  const iMinFace = col('min face', 'minface', 'face min', 'min coverage');
  const iMaxFace = col('max face', 'maxface', 'face max', 'max coverage');
  const iTob = col('tobacco', 'smoker');
  const iTerm = col('term', 'term years', 'terms');
  const iUrl = col('quote url', 'url', 'quoter', 'link');
  const iMode = col('rate mode', 'mode');
  const iFee = col('policy fee', 'fee');
  const iNotes = col('notes', 'note');

  const required: Array<[string, number]> = [
    ['carrier', iCarrier], ['product', iName], ['type', iType], ['underwriting', iUw],
    ['min age', iMinAge], ['max age', iMaxAge], ['min face', iMinFace], ['max face', iMaxFace],
  ];
  for (const [label, idx] of required) {
    if (idx === -1) {
      throw new Error(`The list needs a "${label}" column. Found: ${head.join(', ')}.`);
    }
  }

  const types = new Set(ref.productTypes.map((t) => t.code));
  const kinds = new Set(ref.underwritingKinds.map((t) => t.code));
  const byName = new Map(carriers.map((c) => [c.name.toLowerCase().trim(), c.id]));
  const truthy = (v: string): boolean => ['yes', 'y', 'true', '1', 't'].includes(v.toLowerCase());
  const seen = new Set<string>();

  return lines.slice(1).map((line, i) => {
    const cells = split(line);
    const at = (idx: number): string => (idx === -1 ? '' : (cells[idx] ?? '').trim());
    const where = `Row ${i + 2}`;

    const carrierId = byName.get(at(iCarrier).toLowerCase());
    if (!carrierId) {
      throw new Error(`${where}: no carrier called "${at(iCarrier)}". `
        + `Add it on the Carriers tab first. You have: ${carriers.map((c) => c.name).join(', ')}.`);
    }

    const name = at(iName);
    if (name.length < 2) throw new Error(`${where}: "${name}" is not a product name.`);
    const key = `${carrierId}|${name.toLowerCase()}`;
    if (seen.has(key)) throw new Error(`${where}: "${name}" appears twice for the same carrier.`);
    seen.add(key);

    const productType = at(iType).toLowerCase().replace(/[\s-]+/g, '_');
    if (!types.has(productType)) {
      throw new Error(`${where}: "${at(iType)}" is not a kind of policy. One of: ${[...types].join(', ')}.`);
    }
    const underwriting = at(iUw).toLowerCase().replace(/[\s-]+/g, '_');
    if (!kinds.has(underwriting)) {
      throw new Error(`${where}: "${at(iUw)}" is not an underwriting kind. One of: ${[...kinds].join(', ')}.`);
    }

    const whole = (raw: string, label: string): number => {
      const n = Number(raw.replace(/[,\s]/g, ''));
      if (!Number.isFinite(n) || n < 0) throw new Error(`${where}: "${raw}" is not ${label}.`);
      return Math.trunc(n);
    };
    const minAge = whole(at(iMinAge), 'an age');
    const maxAge = whole(at(iMaxAge), 'an age');
    if (minAge > maxAge) throw new Error(`${where}: issue ages run ${minAge} to ${maxAge}, which is backwards.`);

    const minFace = toCents(at(iMinFace));
    const maxFace = toCents(at(iMaxFace));
    if (minFace === null) throw new Error(`${where}: "${at(iMinFace)}" is not a face amount.`);
    if (maxFace === null) throw new Error(`${where}: "${at(iMaxFace)}" is not a face amount.`);
    if (minFace > maxFace) throw new Error(`${where}: face limits run ${at(iMinFace)} to ${at(iMaxFace)}, which is backwards.`);

    const termYears = at(iTerm)
      ? at(iTerm).split(/[\s/|]+/).filter(Boolean).map((t) => whole(t, 'a term length'))
      : [];

    return {
      carrierId,
      name,
      productType,
      underwriting,
      minAge,
      maxAge,
      minFaceCents: minFace,
      maxFaceCents: maxFace,
      /*
       * The API requires this, so it cannot be left out. Absent, it takes the
       * same default the single-product form shows: yes.
       *
       * That is the permissive direction on purpose. Saying a product writes
       * tobacco when it does not means a smoker sees it listed and then gets
       * "no rate card loaded" — no premium is invented. Saying it does not
       * when it does would silently hide the product from every smoker the
       * agency calls, and nothing on the screen would ever explain why.
       */
      tobaccoAllowed: iTob === -1 || !at(iTob) ? true : truthy(at(iTob)),
      ...(termYears.length ? { termYears } : {}),
      ...(iUrl !== -1 && at(iUrl) ? { quoteUrl: at(iUrl) } : {}),
      /*
       * Also required by the API. Absent, it takes the form's own default —
       * 'table', a premium printed per face amount, which is how final
       * expense cards come. A term product wrongly left on 'table' fails
       * loudly at the next step (its card has no "face" column and the paste
       * is refused, naming the reason) rather than quietly mispricing
       * anything, so the default is safe to have.
       */
      rateMode: iMode !== -1 && at(iMode) ? at(iMode).toLowerCase().replace(/[\s-]+/g, '_') : 'table',
      ...(iFee !== -1 && at(iFee) ? { policyFeeCents: toCents(at(iFee)) ?? 0 } : {}),
      ...(iNotes !== -1 && at(iNotes) ? { notes: at(iNotes) } : {}),
    };
  });
}


